import { Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { kinds, type EnvKey } from '@/app/site/scope';
import type { ShopSettingsBody } from '@/shared/api/generated/model';
import { shopUpdate } from '@/shared/api/generated/shop/shop';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { CopyButton } from '@/shared/ui/CopyButton';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Segmented } from '@/shared/ui/Segmented';
import { Switch } from '@/shared/ui/Switch';
import { toast } from '@/shared/ui/Toast';

import styles from './ShopPage.module.scss';
import { ReviewsBlock } from './ReviewsBlock';
import { PAYMENT_METHODS } from './shop';

/** Ставки НДС для чека — по справочнику ЮKassa. */
const VAT = [
  { value: '1', label: 'Без НДС' },
  { value: '2', label: '0%' },
  { value: '3', label: '10%' },
  { value: '4', label: '20%' },
];

/**
 * Как платят в магазине: тестовая страница Faceta (только Development) или
 * ЮKassa. Настройки — свои у окружения и переносом не едут: на деве — тест, на
 * проде — боевой магазин. Секретный ключ только принимается: пустое поле при
 * сохранении оставляет прежний.
 */
export function ShopSettings({
  siteId,
  env,
  shop,
  onSaved,
}: {
  siteId: string;
  env: EnvKey;
  shop: ShopSettingsBody;
  onSaved: () => void;
}) {
  const [provider, setProvider] = useState<'test' | 'yookassa'>(shop.provider ?? 'test');
  const [shopId, setShopId] = useState(shop.yookassa_shop_id ?? '');
  const [secret, setSecret] = useState('');
  const [reserve, setReserve] = useState(String(shop.reserve_minutes ?? 60));
  const [orderPath, setOrderPath] = useState(shop.order_path ?? '/order');
  const [receipt, setReceipt] = useState(shop.receipt);
  const [vat, setVat] = useState(String(shop.vat_code ?? 1));
  const [tiers, setTiers] = useState<{ from: string; percent: string }[]>(
    (shop.discounts?.loyalty ?? []).map((t) => ({
      from: String(t.from),
      percent: String(t.percent),
    })),
  );
  const birthday = shop.discounts?.birthday;
  const [bday, setBday] = useState(birthday !== undefined);
  const [bdayPercent, setBdayPercent] = useState(String(birthday?.percent ?? 7));
  const [bdayDays, setBdayDays] = useState(String(birthday?.days ?? 5));
  const [bdayField, setBdayField] = useState(birthday?.field ?? 'birthday');
  const [methods, setMethods] = useState<string[]>(shop.payment_methods ?? ['online']);
  const [gifts, setGifts] = useState(shop.gift_cards?.enabled ?? false);
  const [giftMin, setGiftMin] = useState(
    String(shop.gift_cards?.enabled === true ? (shop.gift_cards.min ?? 0) : 1000),
  );
  const [giftMax, setGiftMax] = useState(
    String(shop.gift_cards?.enabled === true ? (shop.gift_cards.max ?? 0) : 50000),
  );
  const [giftMonths, setGiftMonths] = useState(String(shop.gift_cards?.months ?? 12));
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();

    const minutes = Number(reserve);

    if (!Number.isInteger(minutes) || minutes < 5 || minutes > 10080) {
      setError('Резерв — от 5 минут до недели (10080)');

      return;
    }

    const loyalty = tiers
      .filter((t) => t.from.trim() !== '' || t.percent.trim() !== '')
      .map((t) => ({
        from: Number(t.from.replace(',', '.')),
        percent: Number(t.percent.replace(',', '.')),
      }));

    if (loyalty.some((t) => !(t.from >= 0) || !(t.percent > 0 && t.percent <= 100))) {
      setError('Уровень лояльности — сумма от 0 и скидка от 0 до 100%');

      return;
    }

    const birthdayBody = bday
      ? {
          percent: Number(bdayPercent.replace(',', '.')),
          days: Number(bdayDays),
          field: bdayField.trim(),
        }
      : undefined;

    if (
      birthdayBody !== undefined &&
      (!(birthdayBody.percent > 0 && birthdayBody.percent <= 100) ||
        !(Number.isInteger(birthdayBody.days) && birthdayBody.days >= 0 && birthdayBody.days <= 30))
    ) {
      setError('Скидка ко дню рождения — от 0 до 100%, дней — от 0 до 30');

      return;
    }

    const giftBody = {
      enabled: gifts,
      min: Number(giftMin.replace(',', '.')),
      max: Number(giftMax.replace(',', '.')),
      months: Number(giftMonths),
    };

    if (
      gifts &&
      (!(giftBody.min >= 0) ||
        !(giftBody.max > 0) ||
        giftBody.min > giftBody.max ||
        !Number.isInteger(giftBody.months))
    ) {
      setError('Сертификаты: номинал «от» не больше «до», срок — целое число месяцев');

      return;
    }

    if (methods.length === 0) {
      setError('Нужен хотя бы один способ оплаты');

      return;
    }

    setBusy(true);
    setError(undefined);

    shopUpdate(
      siteId,
      kinds[env],
      {
        provider,
        yookassa_shop_id: shopId.trim(),
        ...(secret.trim() === '' ? {} : { yookassa_secret: secret.trim() }),
        reserve_minutes: minutes,
        order_path: orderPath.trim(),
        receipt,
        ...(receipt ? { vat_code: Number(vat) } : {}),
        payment_methods: methods,
        gift_cards: giftBody,
        discounts: { loyalty, ...(birthdayBody === undefined ? {} : { birthday: birthdayBody }) },
      },
      { 'If-Match': shop.etag ?? '' },
    )
      .then(() => {
        toast.success('Сохранено');
        onSaved();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.detail : 'Не удалось сохранить');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <form className={styles['form']} onSubmit={submit} noValidate>
      <Field label="Оплата">
        <Segmented
          value={provider}
          onValueChange={(next) => {
            setProvider(next as typeof provider);
          }}
          aria-label="Оплата"
          options={[
            ...(env === 'dev' ? [{ value: 'test', label: 'Тестовая — без настоящих денег' }] : []),
            { value: 'yookassa', label: 'ЮKassa' },
          ]}
        />
      </Field>

      {provider === 'test' ? (
        <p className={styles['muted']}>
          После оформления посетитель попадает на страницу Faceta с кнопками «Оплатить» и
          «Отказаться» — так проверяется вся цепочка: резерв, статус «оплачен», письма
          автоматизаций. На Production тестовой оплаты нет.
        </p>
      ) : (
        <>
          <div className={styles['row']}>
            <Field label="shopId" hint="Личный кабинет ЮKassa → Интеграция → Ключи API">
              <Input
                mono
                value={shopId}
                onChange={(event) => {
                  setShopId(event.target.value);
                }}
              />
            </Field>
            <Field
              label="Секретный ключ"
              hint={
                shop.yookassa_secret_set
                  ? 'Задан. Пусто — оставить прежний'
                  : 'test_… для тестового магазина, live_… для боевого'
              }
            >
              <Input
                mono
                type="password"
                autoComplete="off"
                value={secret}
                placeholder={shop.yookassa_secret_set ? '••••••••' : ''}
                onChange={(event) => {
                  setSecret(event.target.value);
                }}
              />
            </Field>
          </div>

          {shop.hook_url !== undefined && (
            <Field
              label="Адрес для уведомлений"
              hint="Впишите его в ЮKassa: Интеграция → HTTP-уведомления, события payment.waiting_for_capture, payment.succeeded и payment.canceled."
            >
              <div className={styles['copy']}>
                <Input mono readOnly value={shop.hook_url} />
                <CopyButton value={shop.hook_url} label="Скопировать адрес" />
              </div>
            </Field>
          )}

          <Switch checked={receipt} onCheckedChange={setReceipt}>
            Передавать чек по 54-ФЗ — если к магазину ЮKassa подключена касса
          </Switch>

          {receipt && (
            <Field label="НДС в чеке">
              <Segmented value={vat} onValueChange={setVat} aria-label="НДС" options={VAT} />
            </Field>
          )}
        </>
      )}

      <div className={styles['row']}>
        <Field
          label="Резерв неоплаченного заказа, минут"
          hint="Пока заказ ждёт оплаты, товар отложен. Не оплатили — заказ отменится сам."
        >
          <Input
            inputMode="numeric"
            value={reserve}
            onChange={(event) => {
              setReserve(event.target.value);
            }}
          />
        </Field>
        <Field
          label="Страница заказа на сайте"
          hint={
            shop.order_page_url === undefined
              ? 'Туда возвращают после оплаты. У окружения нет адреса — задайте его в «Настройки → Адреса».'
              : `Туда возвращают после оплаты: ${shop.order_page_url}?order=…&token=…`
          }
          error={error}
        >
          <Input
            mono
            value={orderPath}
            onChange={(event) => {
              setOrderPath(event.target.value);
            }}
          />
        </Field>
      </div>

      <h3 className={styles['panel__title']}>Способы оплаты</h3>
      <p className={styles['muted']}>
        Первый отмеченный — по умолчанию. При получении платёж не заводится, а заказ не отменяется
        по сроку резерва — «оплачен» ставите вы переходом статуса.
      </p>
      {PAYMENT_METHODS.map((m) => (
        <Switch
          key={m.id}
          checked={methods.includes(m.id)}
          onCheckedChange={(on) => {
            setMethods(on ? [...methods, m.id] : methods.filter((id) => id !== m.id));
          }}
        >
          {m.label} — {m.hint}
        </Switch>
      ))}

      <ReviewsBlock siteId={siteId} env={env} shop={shop} onChanged={onSaved} />

      <h3 className={styles['panel__title']}>Подарочные сертификаты</h3>
      <Switch checked={gifts} onCheckedChange={setGifts}>
        Продавать на сайте — номинал выбирает покупатель, код уходит получателю после оплаты
      </Switch>
      {gifts && (
        <div className={styles['row']}>
          <Field label="Номинал от, ₽">
            <Input
              inputMode="decimal"
              value={giftMin}
              onChange={(event) => {
                setGiftMin(event.target.value);
              }}
            />
          </Field>
          <Field label="Номинал до, ₽">
            <Input
              inputMode="decimal"
              value={giftMax}
              onChange={(event) => {
                setGiftMax(event.target.value);
              }}
            />
          </Field>
          <Field label="Действует, месяцев" hint="0 — бессрочно">
            <Input
              inputMode="numeric"
              value={giftMonths}
              onChange={(event) => {
                setGiftMonths(event.target.value);
              }}
            />
          </Field>
        </div>
      )}

      <h3 className={styles['panel__title']}>Личная скидка</h3>
      <p className={styles['muted']}>
        По сумме оплаченных заказов вошедшего покупателя: действует наибольший достигнутый уровень,
        со следующего заказа. Гостям не достаётся.
      </p>
      {tiers.map((tier, index) => (
        <div key={index} className={styles['axis']}>
          <Input
            inputMode="decimal"
            aria-label="От суммы покупок, ₽"
            placeholder="от 15000 ₽"
            value={tier.from}
            onChange={(event) => {
              setTiers(tiers.map((t, i) => (i === index ? { ...t, from: event.target.value } : t)));
            }}
          />
          <Input
            inputMode="decimal"
            aria-label="Скидка, %"
            placeholder="5 %"
            value={tier.percent}
            onChange={(event) => {
              setTiers(
                tiers.map((t, i) => (i === index ? { ...t, percent: event.target.value } : t)),
              );
            }}
          />
          <Button
            size="sm"
            variant="ghost"
            iconOnly
            aria-label="Убрать уровень"
            onClick={() => {
              setTiers(tiers.filter((_, i) => i !== index));
            }}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      ))}
      <div>
        <Button
          size="sm"
          variant="ghost"
          iconLeft={<Plus size={14} />}
          onClick={() => {
            setTiers([...tiers, { from: '', percent: '' }]);
          }}
        >
          Уровень
        </Button>
      </div>

      <Switch checked={bday} onCheckedChange={setBday}>
        Скидка ко дню рождения — дата из профиля посетителя
      </Switch>
      {bday && (
        <div className={styles['row']}>
          <Field label="Скидка, %">
            <Input
              inputMode="decimal"
              value={bdayPercent}
              onChange={(event) => {
                setBdayPercent(event.target.value);
              }}
            />
          </Field>
          <Field label="Дней до и после">
            <Input
              inputMode="numeric"
              value={bdayDays}
              onChange={(event) => {
                setBdayDays(event.target.value);
              }}
            />
          </Field>
          <Field label="Поле профиля" hint="Вид «дата», в «Пользователи → Поля профиля»">
            <Input
              mono
              value={bdayField}
              onChange={(event) => {
                setBdayField(event.target.value);
              }}
            />
          </Field>
        </div>
      )}

      {error !== undefined && <p className={styles['error']}>{error}</p>}

      <div>
        <Button type="submit" loading={busy}>
          Сохранить
        </Button>
      </div>
    </form>
  );
}
